import React, { useEffect, useState, useMemo } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";
import { Space, Typography } from "antd";

import TableFilters from "../TableFilters";
import SaveAsNewList from "../SaveAsNewList";
import { AllPatientsTable } from "./AllPatientsTable";
import { BulkActionsContainer } from "../BulkActions";
import { VisitScheduleTable } from "./VisitScheduleTable";
import { HighPrioritiesTable } from "./HighPrioritiesTable";
import { SavedListTable } from "./SavedListTable";
import { AllApplicationsTable } from "./AllApplicationsTable";
import { AIChatView } from "../AIChat/AIChatView";
import {
  selectSelectedView,
  selectFiltersModified,
  selectAllPatientsData,
  selectAllClaimsData,
  selectAllApplicationsData,
  selectPendingRenewalData,
  selectVisitScheduleData,
  selectHighPrioritiesData,
  selectTableColumns,
  selectFilters,
  selectSorting,
  selectFilterList
} from "../../store/selector";
import { selectedViews, selectedViewTypes } from "../../constant/table";
import { PendingRenewalTable } from "./PendingRenewalTable";
import { FilterListForm } from "../../component/FormPopups/FilterListForm";
import { AllClaimsTable } from "./AllClaimsTable";
import { PatientEnrollSentTable } from "./PatientEnrollSent/PatientEnrollSentTable";
import { GRAY_900 } from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";
import ACTIONS from "../../store/action";
import ExportButton from "../ExportButton";
import { parseSearchFormParams } from "../../api/api";
import { MAX_EXPORT_RECORDS, LIST_TYPES, EXCLUDED_EXPORT_COLUMNS } from "../../constant/export";
import { useExportWithTracking } from "../../hooks/useExportWithTracking";
import {
  useStandardizedTracking,
  EventNames,
  ContentTypes,
  FeatureGroups,
  PageKeys,
  EEContentKeys
} from "../../utils/pendo/useStandardizedTracking";

const { Text } = Typography;

const TitleStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .ant-typography {
    color: ${GRAY_900};
    font-weight: ${fontWeights.semibold};
    font-size: ${sizes.medium_xlarge};
  }
`;

const ContainerStyled = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-top: 10px;
  transition: all 0.3s ease;
`;

const TableSectionComponent = ({ selectedView }) => {
  const dispatch = useDispatch();
  const { exportWithTracking, isExporting: isExportingWithTracking } = useExportWithTracking();
  const { track, trackOnce } = useStandardizedTracking();

  useEffect(() => {
    dispatch(ACTIONS.loadPatientsForViews());
  }, [dispatch]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  useEffect(() => {
    setFiltersVisible(false);
  }, [selectedView]);

  // Tracking for page impressions
  useEffect(() => {
    if (selectedView === selectedViews.allPatients) {
      trackOnce(`page-impression-${selectedView}`, () => {
        track(EventNames.PAGE_IMPRESSION, {
          pageKey: PageKeys.ALL_PATIENTS,
          featureGroup: FeatureGroups.ALL_PATIENTS,
          contentType: ContentTypes.PATIENT,
          contentKey: EEContentKeys.ALL_PATIENTS_PAGE,
          additionalInfo: {
            page_name: selectedViewTypes[selectedView]?.description
          }
        });
      });
    }
  }, [selectedView, track, trackOnce]);

  const showFilters = ![selectedViews.savedList, selectedViews.aiChatBot].includes(selectedView);

  // Get data for export
  const filtersModified = useSelector(selectFiltersModified);
  const allPatientsData = useSelector(selectAllPatientsData);
  const allClaimsData = useSelector(selectAllClaimsData);
  const allApplicationsData = useSelector(selectAllApplicationsData);
  const pendingRenewalData = useSelector(selectPendingRenewalData);
  const visitScheduleData = useSelector(selectVisitScheduleData);
  const highPrioritiesData = useSelector(selectHighPrioritiesData);
  const tableColumns = useSelector(selectTableColumns);
  const filters = useSelector(selectFilters);
  const sorting = useSelector(selectSorting);
  const filterList = useSelector(selectFilterList);

  // Extract visible patient IDs for bulk selection - All Patients
  const visiblePatientIds = useMemo(() => {
    return (allPatientsData?.patients || []).map((patient) => patient.patientId).filter(Boolean);
  }, [allPatientsData?.patients]);

  // Extract visible patient IDs for bulk selection - Visit Schedule
  const visibleVisitScheduleIds = useMemo(() => {
    return (visitScheduleData?.patients || []).map((patient) => patient.patientId).filter(Boolean);
  }, [visitScheduleData?.patients]);

  // Extract visible patient IDs for bulk selection - High Priority
  const visibleHighPriorityIds = useMemo(() => {
    return (highPrioritiesData?.patients || []).map((patient) => patient.patientId).filter(Boolean);
  }, [highPrioritiesData?.patients]);

  // Extract visible enrollment IDs for bulk selection - All Enrollments
  const visibleEnrollmentIds = useMemo(() => {
    return (allApplicationsData?.patients || []).map((enrollment) => enrollment.applicationId).filter(Boolean);
  }, [allApplicationsData?.patients]);

  // Extract visible enrollment IDs for bulk selection - Pending Renewals
  const visiblePendingRenewalIds = useMemo(() => {
    return (pendingRenewalData?.patients || []).map((enrollment) => enrollment.applicationId).filter(Boolean);
  }, [pendingRenewalData?.patients]);

  // Get record count based on selected view
  const getRecordCount = () => {
    switch (selectedView) {
      case selectedViews.allPatients:
        return allPatientsData?.totalPatients || 0;
      case selectedViews.allClaims:
        return allClaimsData?.totalPatients || 0;
      case selectedViews.allApplications:
        return allApplicationsData?.totalPatients || 0;
      case selectedViews.pendingRenewal:
        return pendingRenewalData?.totalPatients || 0;
      case selectedViews.visitSchedule:
        return visitScheduleData?.totalPatients || 0;
      case selectedViews.highPriority:
        return highPrioritiesData?.totalPatients || 0;
      default:
        return 0;
    }
  };

  const getListTypeLabel = () => {
    return selectedViewTypes[selectedView]?.description || "";
  };

  const showExportButton = ![selectedViews.savedList, selectedViews.patientActions].includes(selectedView);
  const showSaveAsNewListButton = showFilters && selectedView !== selectedViews.patientActions;

  // Map selectedView to listType for export API
  const getListType = () => {
    switch (selectedView) {
      case selectedViews.allPatients:
      case selectedViews.visitSchedule:
      case selectedViews.highPriority:
        return LIST_TYPES.PATIENTS;
      case selectedViews.allClaims:
        return LIST_TYPES.CLAIMS;
      case selectedViews.allApplications:
      case selectedViews.pendingRenewal:
        return LIST_TYPES.ENROLLMENTS;
      default:
        return LIST_TYPES.PATIENTS;
    }
  };

  const handleExport = async (fileType) => {
    const visibleColumns = Object.keys(tableColumns[selectedView] || {})
      .filter((key) => tableColumns[selectedView][key]?.display)
      .filter((col) => !EXCLUDED_EXPORT_COLUMNS.includes(col));
    const transformedFilters = parseSearchFormParams({
      selectedView,
      page: 1,
      limit: MAX_EXPORT_RECORDS,
      filters: filters || {}
    });

    await exportWithTracking(
      {
        listType: getListType(),
        selectedView: selectedView,
        filters: transformedFilters.filters || {},
        sorting: sorting || {},
        visibleColumns,
        fileType
      },
      {
        pageKey: `${selectedView}`,
        contentType: selectedViewTypes[selectedView]?.description,
        recordCount: getRecordCount(),
        fileType,
        sorting: sorting || {},
        visibleColumns,
        filters: transformedFilters.filters || {},
        isSavedList: !!filterList?.id,
        savedListId: filterList?.id || null,
        savedListName: filterList?.name || null,
        savedListOriginBucket: null
      }
    );
  };

  return (
    <>
      <TitleStyled>
        <Text>{selectedViewTypes[selectedView]?.description}</Text>
        {(showSaveAsNewListButton || showExportButton) && (
          <Space style={{ display: "flex", alignItems: "center", gap: 10, marginTop: "8px" }} size={8}>
            {showExportButton && (
              <ExportButton
                recordCount={getRecordCount()}
                onExport={handleExport}
                isLoading={isExportingWithTracking}
                listType={getListTypeLabel()}
                selectedView={selectedView}
              />
            )}
            {showSaveAsNewListButton && <SaveAsNewList id="filters_saves_as_new_list" disabled={!filtersModified} />}
          </Space>
        )}
      </TitleStyled>

      <ContainerStyled>
        {showFilters && (
          <div
            style={{
              flex: "0 0 288px",
              maxWidth: filtersVisible ? "288px" : "0px",
              transition: "max-width 0.3s ease"
            }}
          >
            <TableFilters filtersVisible={filtersVisible} setFiltersVisible={setFiltersVisible} />
          </div>
        )}
        <div style={{ flex: "1 1 auto", overflow: "hidden" }}>
          {selectedView === selectedViews.allPatients && (
            <BulkActionsContainer
              viewKey="all-patients"
              entityType="patient"
              visibleRecordIds={visiblePatientIds}
              totalRecords={allPatientsData?.totalPatients || 0}
              rowKey="patientId"
            >
              {({ rowSelection }) => <AllPatientsTable isSearch={false} rowSelection={rowSelection} />}
            </BulkActionsContainer>
          )}
          {selectedView === selectedViews.visitSchedule && (
            <BulkActionsContainer
              viewKey="visit-schedule"
              entityType="patient"
              visibleRecordIds={visibleVisitScheduleIds}
              totalRecords={visitScheduleData?.totalPatients || 0}
              rowKey="patientId"
            >
              {({ rowSelection }) => <VisitScheduleTable rowSelection={rowSelection} />}
            </BulkActionsContainer>
          )}
          {selectedView === selectedViews.pendingRenewal && (
            <BulkActionsContainer
              viewKey="pending-renewal"
              entityType="enrollment"
              visibleRecordIds={visiblePendingRenewalIds}
              totalRecords={pendingRenewalData?.totalPatients || 0}
              rowKey="applicationId"
            >
              {({ rowSelection }) => <PendingRenewalTable rowSelection={rowSelection} />}
            </BulkActionsContainer>
          )}
          {selectedView === selectedViews.highPriority && (
            <BulkActionsContainer
              viewKey="high-priority"
              entityType="patient"
              visibleRecordIds={visibleHighPriorityIds}
              totalRecords={highPrioritiesData?.totalPatients || 0}
              rowKey="patientId"
            >
              {({ rowSelection }) => <HighPrioritiesTable rowSelection={rowSelection} />}
            </BulkActionsContainer>
          )}
          {selectedView === selectedViews.savedList && <SavedListTable />}
          {selectedView === selectedViews.allApplications && (
            <BulkActionsContainer
              viewKey="all-applications"
              entityType="enrollment"
              visibleRecordIds={visibleEnrollmentIds}
              totalRecords={allApplicationsData?.totalPatients || 0}
              rowKey="applicationId"
            >
              {({ rowSelection }) => <AllApplicationsTable rowSelection={rowSelection} />}
            </BulkActionsContainer>
          )}
          {selectedView === selectedViews.allClaims && <AllClaimsTable />}
          {selectedView === selectedViews.patientActions && <PatientEnrollSentTable />}
          {selectedView === selectedViews.aiChatBot && <AIChatView />}
        </div>
        <FilterListForm />
      </ContainerStyled>
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  selectedView: selectSelectedView
});

const TableSection = connect(mapStateToProps)(TableSectionComponent);

export { TableSection };
